import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEOBreadcrumb } from '@/components/SEOBreadcrumb';
import {
  Calendar,
  MapPin,
  Clock,
  Users,
  ExternalLink,
  Share2,
  Loader2,
  CheckCircle,
  XCircle,
  User,
  ArrowRight,
  Monitor,
  MapPinned,
  RefreshCw,
  CalendarDays,
  UserPlus,
  Flag,
} from 'lucide-react';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalBadge } from '@/components/ui/BrutalBadge';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { format, isSameDay } from 'date-fns';
import { it, enUS, es } from 'date-fns/locale';
import { useProfessionalCategories, getCategoryByIdFromData } from '@/hooks/useProfessionalCategories';
import { getCategoryById } from '@/data/categories';
import EventLocationMap from '@/components/events/EventLocationMap';
import { SocialShareDropdown } from '@/components/SocialShareDropdown';
import LoginGate from '@/components/auth/LoginGate';
import { SEO } from '@/components/SEO';
import { InviteFriendModal } from '@/components/events/InviteFriendModal';
import { sanitizeHtml } from '@/lib/sanitize';
import { ReportModal } from '@/components/reports/ReportModal';

interface Organizer {
  id: string;
  slug: string;
  first_name: string | null;
  last_name: string | null;
  business_name: string;
  profile_image: string | null;
  categories: string[] | null;
}

const EventDetail = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<any>(null);
  const [organizer, setOrganizer] = useState<Organizer | null>(null);
  const [registration, setRegistration] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const localeMap = { it, en: enUS, es };
  const locale = localeMap[i18n.language as keyof typeof localeMap] || it;

  // Fetch DB categories for dynamic lookup
  const { data: dbCategories } = useProfessionalCategories(true);

  // Helper to get localized category name - prioritize DB, fallback to static
  const getCategoryName = (categoryId: string) => {
    const lang = (i18n.language || 'it').substring(0, 2) as 'it' | 'en' | 'es';
    // Try DB categories first
    if (dbCategories?.length) {
      const dbCat = getCategoryByIdFromData(dbCategories, categoryId);
      if (dbCat) {
        return dbCat[lang]?.name || dbCat.it?.name || categoryId;
      }
    }
    // Fallback to static categories
    const staticCat = getCategoryById(categoryId);
    if (staticCat) {
      return staticCat[lang]?.name || staticCat.it.name;
    }
    return categoryId;
  };

  useEffect(() => {
    loadEvent();
  }, [slug, user]);

  const loadEvent = async () => {
    try {
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('slug', slug)
        .single();

      if (eventError) throw eventError;
      if (!eventData) {
        navigate('/404');
        return;
      }

      setEvent(eventData);

      // Fetch organizer profile
      const { data: organizerData } = await supabase
        .from('profiles')
        .select('id, slug, first_name, last_name, business_name, profile_image, categories')
        .eq('user_id', eventData.organizer_id)
        .single();

      if (organizerData) {
        setOrganizer(organizerData);
      }

      if (user && eventData.event_type === 'free') {
        const { data: regData } = await supabase
          .from('event_registrations')
          .select('*')
          .eq('event_id', eventData.id)
          .eq('user_id', user.id)
          .maybeSingle();

        setRegistration(regData);
      }
    } catch (error: any) {
      console.error('Error loading event:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
      navigate('/404');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!isAuthenticated) {
      toast({
        title: t('auth.loginRequired'),
        description: t('auth.pleaseLogin'),
        variant: 'error',
      });
      navigate('/auth/login');
      return;
    }

    if (!user) return;

    setRegistering(true);
    try {
      const newStatus = event.max_participants && event.current_participants >= event.max_participants
        ? 'waitlist'
        : 'registered';

      let registrationId: string | null = null;

      // Se esiste una registrazione cancellata, aggiorna invece di inserire
      if (registration && registration.registration_status === 'cancelled') {
        const { error } = await supabase
          .from('event_registrations')
          .update({
            registration_status: newStatus,
            registration_date: new Date().toISOString(),
            cancellation_date: null,
            cancellation_reason: null,
          })
          .eq('id', registration.id);

        if (error) throw error;
        registrationId = registration.id;
      } else {
        const { data: newReg, error } = await supabase
          .from('event_registrations')
          .insert({
            event_id: event.id,
            user_id: user.id,
            registration_status: newStatus,
          })
          .select('id')
          .single();

        if (error) throw error;
        registrationId = newReg?.id;
      }

      // Invia email di conferma registrazione
      if (registrationId) {
        try {
          await supabase.functions.invoke('send-event-confirmation', {
            body: { registration_id: registrationId }
          });
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError);
          // Non bloccare se l'email fallisce
        }
      }

      toast({
        title: t('events.registered'),
        description: t('events.registeredDesc'),
      });

      loadEvent();
    } catch (error: any) {
      console.error('Error registering:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!registration) return;

    try {
      const { error } = await supabase
        .from('event_registrations')
        .update({
          registration_status: 'cancelled',
          cancellation_date: new Date().toISOString(),
        })
        .eq('id', registration.id);

      if (error) throw error;

      toast({
        title: t('events.cancelled'),
        description: t('events.cancelledDesc'),
      });

      setShowCancelDialog(false);
      loadEvent();
    } catch (error: any) {
      console.error('Error cancelling:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    }
  };

  const getDescription = () => {
    if (!event) return '';
    if (i18n.language === 'it') return event.description_it;
    if (i18n.language === 'en') return event.description_en;
    if (i18n.language === 'es') return event.description_es;
    return event.description_it;
  };


  // Check if event spans multiple days
  const isMultiDay = event && !isSameDay(new Date(event.start_datetime), new Date(event.end_datetime));

  const isOrganizer = user?.id === event?.organizer_id;
  const isEventFull =
    event?.max_participants && event.current_participants >= event.max_participants;
  const isCancelled = registration?.registration_status === 'cancelled';
  const canRegister =
    event?.event_type === 'free' && (!registration || isCancelled) && !isEventFull && !isOrganizer;
  const isWaitlist = registration?.registration_status === 'waitlist';
  const isRegistered =
    registration?.registration_status === 'registered' ||
    registration?.registration_status === 'attended';

  // Get format badge info
  const getFormatBadge = () => {
    if (!event?.event_format) return null;
    const formatConfig: Record<string, { icon: React.ReactNode; label: string; variant: 'default' | 'secondary' | 'success' | 'warning' }> = {
      online: { icon: <Monitor className="w-3 h-3" />, label: t('events.formatBadge.online'), variant: 'secondary' },
      in_person: { icon: <MapPinned className="w-3 h-3" />, label: t('events.formatBadge.inPerson'), variant: 'default' },
      hybrid: { icon: <RefreshCw className="w-3 h-3" />, label: t('events.formatBadge.hybrid'), variant: 'warning' },
    };
    return formatConfig[event.event_format];
  };

  // Get type badge info
  const getTypeBadge = () => {
    if (!event?.event_type) return null;
    const typeConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' }> = {
      free: { label: t('events.typeBadge.free'), variant: 'success' },
      paid: { label: t('events.typeBadge.paid'), variant: 'default' },
      external: { label: t('events.typeBadge.external'), variant: 'secondary' },
    };
    return typeConfig[event.event_type];
  };

  const formatBadge = getFormatBadge();
  const typeBadge = getTypeBadge();

  // SEO: Generate dynamic meta data for event - MUST be before conditional returns
  const seoData = useMemo(() => {
    if (!event) {
      return { title: '', description: '', schema: null };
    }
    
    const lang = i18n.language.substring(0, 2) as 'it' | 'en' | 'es';
    const eventDate = format(new Date(event.start_datetime), 'PPP', { locale });
    const location = event.event_format === 'online' 
      ? 'Online' 
      : event.location_city || '';
    const locationText = location ? ` ${lang === 'it' ? 'a' : lang === 'es' ? 'en' : 'in'} ${location}` : '';
    const eventDescription = event.description_it || event.description_en || event.description_es || '';
    const descPreview = eventDescription.substring(0, 100);
    
    const title = `${event.title} - ${lang === 'it' ? 'Evento Inclusivo' : lang === 'es' ? 'Evento Inclusivo' : 'Inclusive Event'}`;
    const description = `${event.title} - ${eventDate}${locationText}. ${descPreview}${descPreview.length >= 100 ? '...' : ''} ${lang === 'it' ? 'Scopri eventi inclusivi su Inclusia.' : lang === 'es' ? 'Descubre eventos inclusivos en Inclusia.' : 'Discover inclusive events on Inclusia.'}`;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": event.title,
      "description": eventDescription,
      "image": event.cover_image_url,
      "url": `${window.location.origin}/eventi/${event.slug}`,
      "startDate": event.start_datetime,
      "endDate": event.end_datetime,
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": event.event_format === 'online' 
        ? "https://schema.org/OnlineEventAttendanceMode"
        : event.event_format === 'hybrid'
        ? "https://schema.org/MixedEventAttendanceMode"
        : "https://schema.org/OfflineEventAttendanceMode",
      ...(event.event_format !== 'online' && event.location_name && {
        "location": {
          "@type": "Place",
          "name": event.location_name,
          "address": {
            "@type": "PostalAddress",
            ...(event.location_address && { "streetAddress": event.location_address }),
            ...(event.location_city && { "addressLocality": event.location_city })
          },
          ...(event.location_latitude && event.location_longitude && {
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": event.location_latitude,
              "longitude": event.location_longitude
            }
          })
        }
      }),
      ...(event.event_format === 'online' && event.location_url && {
        "location": {
          "@type": "VirtualLocation",
          "url": event.location_url
        }
      }),
      ...(organizer && {
        "organizer": {
          "@type": "Person",
          "name": organizer.business_name,
          "url": `${window.location.origin}/professionista/${organizer.slug}`
        }
      }),
      ...(event.price !== null && event.price !== undefined && {
        "offers": {
          "@type": "Offer",
          "price": event.price,
          "priceCurrency": event.currency || "EUR",
          "availability": event.max_participants && event.current_participants >= event.max_participants
            ? "https://schema.org/SoldOut"
            : "https://schema.org/InStock"
        }
      })
    };
    
    return { title, description, schema };
  }, [event, organizer, i18n.language, locale]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="h-96 w-full mb-8 border-[4px] border-dark bg-muted animate-pulse shadow-[8px_8px_0px_0px] shadow-dark" />
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="h-64 border-[3px] border-dark bg-muted animate-pulse shadow-[6px_6px_0px_0px] shadow-dark" />
            </div>
            <div className="h-64 border-[3px] border-dark bg-muted animate-pulse shadow-[6px_6px_0px_0px] shadow-dark" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seoData.title}
        description={seoData.description}
        image={event.cover_image_url || undefined}
        type="article"
        lang={i18n.language.substring(0, 2) as 'it' | 'en' | 'es'}
        schema={seoData.schema}
      />
      <Header />
      {/* Cover Image - Full Width outside container */}
      {event.cover_image_url && (
        <div className="w-full mb-8">
          <img
            src={event.cover_image_url}
            alt={event.title}
            className="w-full h-auto border-b-[4px] border-dark"
            loading="lazy"
          />
        </div>
      )}

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">

        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content - Single Box */}
          <div className="md:col-span-2">
            <div className="border-[3px] border-dark p-6 bg-card shadow-[6px_6px_0px_0px] shadow-dark">
              {/* Breadcrumb */}
              <SEOBreadcrumb 
                items={[
                  { label: t('common.events'), href: '/events' },
                  { label: event.title }
                ]}
                align="left"
              />
              {/* Title with Report Flag */}
              <div className="flex items-start justify-between gap-4 mt-4 mb-3">
                <h1 className="text-3xl md:text-4xl font-heading font-black text-foreground">
                  {event.title}
                </h1>
                {user && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setShowReportModal(true)}
                          className="p-2 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                          aria-label={t('reports.reportEvent')}
                        >
                          <Flag className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('reports.reportEvent')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {/* Pills sotto al titolo */}
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {formatBadge && (
                  <Badge className="bg-primary text-primary-foreground text-[10px] font-bold uppercase px-2 py-0.5 border-0 shrink-0 flex items-center gap-1">
                    {formatBadge.icon}
                    {formatBadge.label}
                  </Badge>
                )}
                {typeBadge && (
                  <Badge className={
                    event.event_type === 'free'
                      ? "bg-emerald-500 text-white text-[10px] font-bold uppercase px-2 py-0.5 border-0 shrink-0"
                      : "bg-blue-500 text-white text-[10px] font-bold uppercase px-2 py-0.5 border-0 shrink-0"
                  }>
                    {typeBadge.label}
                  </Badge>
                )}
                {event.is_featured && (
                  <Badge className="bg-amber-500 text-primary-foreground text-[10px] font-bold uppercase px-2 py-0.5 border-0 shrink-0">
                    {t('events.featured')}
                  </Badge>
                )}
              </div>

              {/* Event Info Section - Protected by LoginGate */}
              <LoginGate 
                title={t('auth.gate.viewEventDetails')} 
                description={t('auth.gate.registerFreeToSeeDetails')}
                variant="inline"
              >
                <div className="border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark p-4 md:p-6 mb-6">
                  <div className={`grid gap-6 ${
                    (event.event_format === 'in_person' || event.event_format === 'hybrid') && 
                    event.location_latitude && event.location_longitude 
                      ? 'grid-cols-1 lg:grid-cols-2' 
                      : 'grid-cols-1'
                  }`}>
                    {/* Left Column - Details */}
                    <div className="space-y-4">
                      {/* Date */}
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5 text-primary" />
                        <div>
                          <p className="font-heading font-bold uppercase text-sm text-dark/60">{t('events.date')}</p>
                          {isMultiDay ? (
                            <p className="font-body">
                              {format(new Date(event.start_datetime), 'PPP', { locale })} → {format(new Date(event.end_datetime), 'PPP', { locale })}
                            </p>
                          ) : (
                            <p className="font-body">{format(new Date(event.start_datetime), 'PPP', { locale })}</p>
                          )}
                        </div>
                      </div>

                      {/* Time */}
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 flex-shrink-0 mt-0.5 text-primary" />
                        <div>
                          <p className="font-heading font-bold uppercase text-sm text-dark/60">{t('events.time')}</p>
                          <p className="font-body">
                            {format(new Date(event.start_datetime), 'p', { locale })} - {format(new Date(event.end_datetime), 'p', { locale })}
                          </p>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5 text-primary" />
                        <div>
                          <p className="font-heading font-bold uppercase text-sm text-dark/60">{t('events.location')}</p>
                          {event.event_format === 'online' ? (
                            <div className="space-y-2">
                              <p className="font-body">{t('events.onlineEvent')}</p>
                              {event.location_url && (
                                <a 
                                  href={event.location_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-primary hover:underline font-body"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  {t('events.joinMeeting')}
                                </a>
                              )}
                            </div>
                          ) : (
                            <div className="font-body">
                              {event.location_name && <p className="font-bold">{event.location_name}</p>}
                              {event.location_address && <p>{event.location_address}</p>}
                              {event.location_city && <p>{event.location_city}</p>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Map (only for in_person/hybrid with coordinates) */}
                    {(event.event_format === 'in_person' || event.event_format === 'hybrid') && 
                     event.location_latitude && event.location_longitude && (
                      <div className="lg:pl-6">
                        <EventLocationMap 
                          latitude={event.location_latitude}
                          longitude={event.location_longitude}
                          locationName={event.location_name || undefined}
                          locationAddress={event.location_address || undefined}
                          locationCity={event.location_city || undefined}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </LoginGate>

              {/* Description Section */}
              <div className="pt-4">
                <h2 className="font-heading font-bold text-lg mb-4">{t('events.eventDetails', { name: event.title })}</h2>
                {getDescription() && (
                  <div 
                    className="prose prose-sm max-w-none font-body"
                    dangerouslySetInnerHTML={{ 
                      __html: sanitizeHtml(getDescription() || '', {
                        allowedTags: ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'h2', 'blockquote']
                      }) 
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Unified Registration + Organizer Card */}
            <div className="border-[3px] border-dark p-6 bg-card shadow-[6px_6px_0px_0px] shadow-dark">
              {/* Participants count */}
              {event.max_participants && (
                <div className="flex items-center gap-2 text-sm font-body mb-4">
                  <Users className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {event.current_participants}/{event.max_participants}{' '}
                    {t('events.participants')}
                  </span>
                </div>
              )}

              {/* Registration / Purchase section */}
              <div className="space-y-4">
                {isOrganizer ? (
                  <div className="space-y-3">
                    <BrutalButton className="w-full" disabled variant="outline">
                      <User className="w-4 h-4 mr-2" />
                      {t('events.yourEvent')}
                    </BrutalButton>
                    <BrutalButton
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowInviteModal(true)}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      {t('events.inviteFriend.button')}
                    </BrutalButton>
                  </div>
                ) : event.event_type === 'free' ? (
                  <>
                    {isRegistered && (
                      <div className="space-y-3">
                        <BrutalBadge variant="success" className="w-full justify-center py-2">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {t('events.youAreRegistered')}
                        </BrutalBadge>
                        <BrutalButton
                          variant="outline"
                          className="w-full"
                          onClick={() => setShowInviteModal(true)}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          {t('events.inviteFriend.button')}
                        </BrutalButton>
                        <BrutalButton
                          variant="outline"
                          className="w-full"
                          onClick={() => setShowCancelDialog(true)}
                        >
                          {t('events.cancelRegistration')}
                        </BrutalButton>
                      </div>
                    )}
                    {isWaitlist && (
                      <BrutalBadge variant="warning" className="w-full justify-center py-2">
                        <Clock className="w-4 h-4 mr-2" />
                        {t('events.onWaitlist')}
                      </BrutalBadge>
                    )}
                    {canRegister && (
                      <BrutalButton
                        className="w-full"
                        onClick={handleRegister}
                        disabled={registering}
                      >
                        {registering ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : null}
                        {t('events.register')}
                      </BrutalButton>
                    )}
                    {isEventFull && !registration && (
                      <BrutalButton className="w-full" disabled>
                        <XCircle className="w-4 h-4 mr-2" />
                        {t('events.full')}
                      </BrutalButton>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="text-center pb-3">
                      <p className="text-2xl font-heading font-bold uppercase">
                        {event.price} {event.currency}
                      </p>
                      {event.external_platform && (
                        <p className="text-sm font-body text-dark/70">
                          {t('events.via')} {event.external_platform}
                        </p>
                      )}
                    </div>
                    <a
                      href={event.external_ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full items-center justify-center font-heading font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed border-[3px] border-dark bg-primary text-dark shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] hover:shadow-dark active:translate-x-[4px] active:translate-y-[4px] active:shadow-[2px_2px_0px_0px] px-6 py-3 text-base"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {t('events.buyTicket')}
                    </a>
                    <p className="text-xs font-body text-dark/60 text-center">
                      {t('events.externalPlatformDisclaimer')}
                    </p>
                  </div>
                )}
              </div>

              {/* Organizer section */}
              {organizer && (
                <div className="mt-4 pt-4">
                  <h3 className="font-heading font-bold uppercase mb-4">{t('events.organizer')}</h3>
                  
                  <Link 
                    to={`/professional/${organizer.slug}`} 
                    className="flex items-center gap-4 group"
                  >
                    {/* Avatar */}
                    {organizer.profile_image ? (
                      <img 
                        src={organizer.profile_image} 
                        alt={`${organizer.first_name} ${organizer.last_name}`}
                        className="w-16 h-16 rounded-full object-cover border-[2px] border-dark flex-shrink-0" 
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center border-[2px] border-dark flex-shrink-0">
                        <User className="w-8 h-8 text-dark/50" />
                      </div>
                    )}
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold text-dark group-hover:text-primary transition-colors truncate">
                        {organizer.first_name} {organizer.last_name}
                      </p>
                      {organizer.categories?.[0] && (
                        <p className="text-sm font-body text-dark/70 truncate">
                          {getCategoryName(organizer.categories[0])}
                        </p>
                      )}
                    </div>
                    
                    <ArrowRight className="w-5 h-5 text-dark/50 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </Link>
                </div>
              )}
            </div>

            {/* Share Button - standalone */}
            <SocialShareDropdown 
              url={`${window.location.origin}/event/${event.slug}`}
              title={event.title}
              description={getDescription() || ''}
              buttonText={t('events.shareEvent')}
              fullWidth
            />
          </div>
        </div>
        </div>
      </main>
      <Footer />

      {/* Cancel Registration Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('events.cancelRegistrationTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('events.cancelRegistrationDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <BrutalButton variant="outline">{t('common.close')}</BrutalButton>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <BrutalButton onClick={handleCancelRegistration}>
                {t('events.confirmCancel')}
              </BrutalButton>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite Friend Modal */}
      {event && (
        <InviteFriendModal
          eventId={event.id}
          eventTitle={event.title}
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        eventId={event?.id}
        targetName={event?.title || ''}
      />
    </div>
  );
};

export default EventDetail;
